import { Dispatch, RefObject, SetStateAction, useState } from 'react'

import { useTranslate } from '@marketplace-web/shared/i18n'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { itemUploadClickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import useMediaSelectContext from './useMediaSelectContext'

const joinText = (...text: Array<string>) => text.join(' ')

type Props = {
  setIsControlsHidden: Dispatch<SetStateAction<boolean>>
  containerRef: RefObject<HTMLDivElement | null>
  photosCount: number
  uploadSessionId?: string
  screen: Screen
}

const useMediaSelectSort = ({
  setIsControlsHidden,
  containerRef,
  photosCount,
  uploadSessionId,
  screen,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('item_upload.media_select')
  const { changePhotoOrder } = useMediaSelectContext()

  const [ariaText, setAriaText] = useState('')

  const handleSortStart = (index: number) => {
    setAriaText(
      joinText(
        translate('a11y.uploaded_image_grabbed', { count: index + 1 }),
        translate('a11y.current_order_position', { position: index + 1, total: photosCount }),
        translate('a11y.active_reorder'),
      ),
    )

    setIsControlsHidden(true)
  }

  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    containerRef.current?.focus()
    setIsControlsHidden(false)

    if (oldIndex === newIndex) {
      setAriaText(
        joinText(
          translate('a11y.uploaded_image_cancelled', { count: oldIndex + 1 }),
          translate('a11y.current_order_position', { position: newIndex + 1, total: photosCount }),
          translate('a11y.inactive_reorder'),
        ),
      )

      return
    }

    setAriaText(
      joinText(
        translate('a11y.uploaded_image_saved', { count: oldIndex + 1 }),
        translate('a11y.current_order_position', { position: newIndex + 1, total: photosCount }),
        translate('a11y.inactive_reorder'),
      ),
    )

    if (uploadSessionId) {
      track(
        itemUploadClickEvent({
          uploadSessionId,
          target: ClickableElement.RearrangeMedia,
          screen,
        }),
      )
    }
    changePhotoOrder({ oldIndex, newIndex })
  }

  const handleSortOver = (oldIndex: number, newIndex: number) => {
    setAriaText(
      joinText(
        translate('a11y.uploaded_image_moved', { count: oldIndex + 1 }),
        translate('a11y.current_order_position', { position: newIndex + 1, total: photosCount }),
        translate('a11y.active_reorder'),
      ),
    )
  }

  return {
    ariaText,
    handleSortStart,
    handleSortEnd,
    handleSortOver,
  }
}

export default useMediaSelectSort
